import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import useApi from '../../hooks/useApi';
import Draggable from 'react-draggable';
import ThemeDropdownWithPreview from '../themesDropdown';
import posthog from 'posthog-js';

function BasicTheme({ book_id }) {
  
  const [pages, setPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [targetPage, setTargetPage] = useState(0);
  const { request, loading, error } = useApi();
  const [totalPages, setTotalPages] = useState(0);
  const [pageSlotStart, setPageSlotStart] = useState(0);
  const [pageSlotEnd, setPageSlotEnd] = useState(0);
  const [SnapshotTotalPages, setSnapshotTotalPages] = useState(0);
  const pageMap = useMemo(() => {
    return Object.fromEntries(pages.map(p => [p.page_order, p]));
  }, [pages]);
  //const getPage = (index) => (index >= 0 && index < pages.length ? pages[index] : null);
  const getPage = (order) => pageMap[order] || null;
  const leftPage = getPage(currentPage * 2+1);
  const rightPage = getPage(currentPage * 2 + 1+1);
  const previousLeftPage = getPage((currentPage - 1) * 2+1);
  const nextRightPage = getPage((currentPage + 1) * 2 + 1+1);
  const [initialLoading, setInitialLoading] = useState(true);
  const textRefs = useRef([]); 
  const [textHeights, setTextHeights] = useState({});
  const [zoomedImage, setZoomedImage] = useState(null);
  const [editingPages, setEditingPages] = useState({});
  const [draftTexts, setDraftTexts] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pageToDelete, setPageToDelete] = useState(null);
  const [pageStatus, setPageStatus] = useState({}); 
  const [viewMode, setViewMode] = useState('enhanced'); // 'enhanced' or 'basic'
  const [snapshots, setSnapshots] = useState([]);
  const [fade, setFade] = useState(true);
  const [loadingSnapshots, setLoadingSnapshots] = useState(false);
  const [loadedSnapshots, setLoadedSnapshots] = useState({});
  const [lastPageRead, setLastPageRead] = useState(0);
  const [showSettings, setShowSettings] = useState(false);
  const [audioVolume, setAudioVolume] = useState(0.5);
  const [isMuted, setIsMuted] = useState(false);
  const audioRef = useRef(null);
  const [backgroundThemes, setBackgroundThemes] = useState([]);
  const [pageThemes, setPageThemes] = useState([]);
  const getBackgroundTheme = (index) => (index >= 0 && index < backgroundThemes.length ? backgroundThemes[index] : null);
  const getPageTheme = (index) => (index >= 0 && index < pageThemes.length ? pageThemes[index] : null);
  const [selectedPageTheme, setSelectedPageTheme] = useState(getPageTheme(0));
  const [selectedBackgroundTheme, setSelectedBackgroundTheme] = useState(getBackgroundTheme(0));
  const [previewTheme, setPreviewTheme] = useState({});
  const [loadingTheme, setLoadingTheme] = useState(null);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  let audio = null;

  const fetchInitialPages = useCallback(async () => {
    try {
      const data = await request({
        method: 'get',
        url: `/api/v1/pdf_parser/books/${book_id}/`,
        params: { start: 0, end: 19 },
      });
  
      console.log('Initial API Response:', data);
  
      if (data) {
        setTotalPages(parseInt(data.book.poppyruz_pages_count));
        setSnapshotTotalPages(parseInt(data.book.original_pages_count));
        setPages(data.pages);
        setPageSlotStart(0);
        setPageSlotEnd(19);
      }
    } catch (err) {
      console.error('Initial API Error:', err);
    } finally {
      setInitialLoading(false);
    }
  }, [request]);

  const fetchNextPages = async (start, end) => {
    const alreadyFetched = Array.from({ length: end - start + 1 }, (_, i) => start + i)
    .every(num => pages.some(p => p.page_number === num));
    if (alreadyFetched) return;
    try {
      const data = await request({
        method: 'get',
        url: `/api/v1/pdf_parser/books/${book_id}/`,
        params: { start, end },
      });

      console.log(`Fetched pages ${start}–${end}:`, data?.pages?.map(p => p.page_number));

      if (data) {
        setPages(prevPages => prevPages.concat(data.pages));
      }
    } catch (err) {
      console.error('API Error:', err);
    }finally {
      setInitialLoading(false);
    }
  };

  const fetchPreviousPages = async (start, end) => {
    const alreadyFetched = Array.from({ length: end - start + 1 }, (_, i) => start + i)
    .every(num => pages.some(p => p.page_number === num));
    if (alreadyFetched) return;
    try {
      const data = await request({
        method: 'get',
        url: `/api/v1/pdf_parser/books/${book_id}/`,
        params: { start, end },
      });
      if (data) {
        setPages(prevPages => data.pages.concat(prevPages));
      }
    } catch (err) {
      console.error('API Error:', err);
    }
  };

  const updateUserBookDetail = async ({theme_id,last_read_page}) => {
    try {
      const data = await request({
        method: 'patch',
        url: `/api/v1/pdf_parser/my_book_view/${book_id}/`,
        withAuth:true,
        data:{
          theme_id : theme_id,
          last_read_page : last_read_page,
        }
      });
    } catch (err) {
      console.error('API Error:', err);
    }
  };

  const fetchSnapshots = async (start, end) => {
    try {
      const data = await request({
        method: 'get',
        url: `/api/v1/pdf_parser/snapshots/${book_id}/`,
        params: { start, end },
      });
      if (data) {
        const joinedNewSnapsList = snapshots.concat(data.snapshots);
        setSnapshots(joinedNewSnapsList);
        console.log(snapshots);
      }
    } catch (err) {
      console.error('API Error:', err);
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const token = localStorage.getItem('access_token');
        let user_id = 'guest';
        if (token !== null) {
          user_id = localStorage.getItem('user_id');
        }
        const themeData = await request({
          method: 'get',
          url: `/api/v1/pdf_parser/themes/${user_id}/`,
        });
        if (!themeData) throw new Error("No theme data");
        const backgroundThemes = themeData.book_themes || [];
        const pageThemes = themeData.page_themes || [];
        setBackgroundThemes(backgroundThemes);
        setPageThemes(pageThemes);
        setSelectedBackgroundTheme(themeData.book_themes[0]);
        console.log('themes are loaded');
        if (token !== null) {
          const userData = await request({
            method: 'get',
            url: `/api/v1/pdf_parser/my_book_view/${book_id}/`,
            withAuth: true
          });
          if (userData?.book_detail) {
            setCurrentPage(userData.book_detail.last_read_page);
            setTargetPage(userData.book_detail.last_read_page);
            setTotalPages(parseInt(userData.book_detail.poppyruz_pages_count));
            setSnapshotTotalPages(parseInt(userData.book_detail.original_pages_count));
            const matchedTheme = backgroundThemes.find(t => t.theme_meta_unique_id === userData.book_detail.theme_id);
            if (matchedTheme) {
              handleBackgroundThemeChange(matchedTheme);
            } else {
              console.warn('No matching theme found for theme_id:', userData.book_detail.theme_id);
            }
            if (userData.book_detail.last_read_page !== 0) {
                const start = (userData.book_detail.last_read_page * 2) - 10;
                const end = (userData.book_detail.last_read_page * 2) + 9;
              if (start < 0) {
                fetchInitialPages();
              } else {
                fetchNextPages(start, end);
                setPageSlotStart(start);
                setPageSlotEnd(end);
              }
            } else {
              fetchInitialPages();
            }
          }
        } else {
          console.log('Initial load (guest)');
          fetchInitialPages();
        }
      } catch (err) {
        console.error('Error loading initial data:', err);
        fetchInitialPages();
      } 
    };
    loadInitialData();
  }, []);  

  const nextPage = () => {
    if (viewMode === 'basic') {
      if(snapshots.length<SnapshotTotalPages){
        setTargetPage(currentPage + 1);
        if (currentPage == Math.ceil(snapshots.length/2)-5){
            const start = snapshots.length;
            const end = start + 19;
            console.log(start)
            console.log(end)
            if(start < SnapshotTotalPages){
              if(end < SnapshotTotalPages && end != SnapshotTotalPages){
                fetchSnapshots(start, end);
              }else{
                const newEnd = start+((SnapshotTotalPages - start)-1);
                fetchSnapshots(start, newEnd);
              }
            }
        }
      }
      return;
    }
    if((pageSlotEnd+1)<totalPages){
      console.log(pageSlotEnd)
      setTargetPage(currentPage + 1);
      if (currentPage == Math.ceil((pageSlotEnd+1)/2)-3){
        console.log(currentPage)
        const start = pageSlotEnd+1;
        const end = start + 19;
        console.log('total pages')
        console.log(totalPages);
        if(start < totalPages){
          if(end < totalPages && end != totalPages){
            fetchNextPages(start, end);
            setPageSlotEnd(end);
          }else{
            const newEnd = start+((totalPages - start)-1);
            fetchNextPages(start, newEnd);
            setPageSlotEnd(newEnd);
          }
        }
      }
    }
  };

  const previousPage = () => {
    if (targetPage > 0) {
      if(((pageSlotStart+1)/2)<currentPage - 1){
        if(currentPage - 1 == Math.ceil(((pageSlotStart+1)/2)+3)){
          const end = pageSlotStart-1; 
          const start = end - 19;
          if(start > 0){
            fetchPreviousPages(start,end);
            setPageSlotStart(start);
          }else{
            fetchPreviousPages(0,end);
            setPageSlotStart(0);
          }
          setTargetPage(currentPage - 1);
        }else{
          setTargetPage(currentPage - 1);
        }
      }else{
        const end = pageSlotStart-1; 
        const start = end - 19;
        if(start > 0){
          fetchPreviousPages(start,end);
          setPageSlotStart(start);
        }else{
          fetchPreviousPages(0,end);
          setPageSlotStart(0);
        }
        setTargetPage(currentPage - 1);
      }
    }
  };

  useEffect(() => {
    if (audioRef.current) {
        audioRef.current.volume = audioVolume;
        audioRef.current.muted = isMuted;
        if(selectedBackgroundTheme){
          if (selectedBackgroundTheme?.has_audio && selectedBackgroundTheme.audio_url) {
            audioRef.current.src = selectedBackgroundTheme.audio_url;
            audioRef.current.play().catch(() => {});
          } else {
            audioRef.current.pause();
          }
        }
    }
  }, [selectedBackgroundTheme, audioVolume, isMuted]);

  useEffect(() => {
    if (pages.length > 0) {
      const heights = {};
      textRefs.current.forEach((ref, idx) => {
        if (ref) {
          heights[idx] = ref.offsetHeight;
        }
      });
      setTextHeights(heights);
    }
  }, [pages]);

  const deletePage = async (pageIdx) => {
    const pageId = pages[pageIdx]?.page_meta_unique_id;
    setPageStatus(prev => ({ ...prev, [pageIdx]: 'deleting' }));
    try{
      const response = await request({
        method: 'delete',
        url: `/api/v1/pdf_parser/pages/${pageId}/`,
      });
      if (response.success === "true") {
        const updatedPages = [...pages];
        updatedPages.splice(pageIdx, 1);  // Remove 1 item at index pageIdx
        setPages(updatedPages);
      }
    } catch (error) {
      console.error('API error during delete:', error);
      alert('Something went wrong while deleting.');
    } finally {
      setPageStatus(prev => ({ ...prev, [pageIdx]: 'idle' }));
    }
    setEditingPages(prev => {
      const updated = { ...prev };
      delete updated[pageIdx]; // Clean up editing state
      return updated;
    });
  };

  const switchToBasicView = async () => {
    setFade(false);
    setLoadingSnapshots(true);
    setTimeout(async () => {
      try {
        const snapshotData = await request({
          method: 'get',
          url: `/api/v1/pdf_parser/snapshots/${book_id}/`,
          params: { start: 0, end: 19 },
        });
        setSnapshots(snapshotData.snapshots);
        setLastPageRead(currentPage);
        setTargetPage(0);
        setCurrentPage(0);
        setSelectedPageTheme(getPageTheme(0));
        setViewMode('basic');
        setFade(true);
      } catch (err) {
        console.error('Failed to load snapshots', err);
        alert('Failed to switch to basic view!');
        setFade(true);
      } finally {
        setLoadingSnapshots(false);
      }
    },300);
    posthog.capture('switch_to_basic_view', { book_id, last_page_read: currentPage });
  };

  const backToEnhancedView = () => {
    setFade(false);
    setTargetPage(lastPageRead);
    setTimeout(() => {
      setViewMode('enhanced');
      setFade(true);
    }, 300);
    posthog.capture('switch_to_enhanced_view', { book_id, return_page: lastPageRead });
  };

  const handlePageThemeChange = (theme) => {
    setLoadingTheme(theme); 
    const img = new Image();
    img.src = theme.theme_url;
    img.onload = () => {
      setSelectedPageTheme(theme); 
      setLoadingTheme(null);            
    };
    img.onerror = () => {
      alert("Failed to load theme image");
      setLoadingTheme(null);
    };
    posthog.capture('change_page_theme', {
      book_id,
      theme_name: theme.theme_name,
      theme_id: theme.theme_meta_unique_id
    });
  };

  const handleBackgroundThemeChange = (theme) => {
    setLoadingTheme(theme); 
    if (theme.has_audio && theme.audio_url) {
      audio = new Audio(theme.audio_url);
    }
    const img = new Image();
    img.src = theme.theme_url;
    img.onload = () => {
      setSelectedBackgroundTheme(theme); 
      setLoadingTheme(null);             
    };
    img.onerror = () => {
      alert("Failed to load theme image");
      setLoadingTheme(null);
    };
    updateUserBookDetail({theme_id:theme.theme_meta_unique_id});
    posthog.capture('change_background_theme', {
      book_id,
      theme_name: theme.theme_name,
      theme_id: theme.theme_meta_unique_id,
      has_audio: theme.has_audio === 1
    });
  };

  const parseVisualContent = (value) => {
    if (!value) return [];
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed;
    } catch (_) {
      try {
        const fixed = value.replace(/'/g, '"');
        const parsed = JSON.parse(fixed);
        if (Array.isArray(parsed)) return parsed;
      } catch (_) {
        
      }
    }
    const matches = value.match(/https?:\/\/[^\s'"]+/g);
    return matches || [];
  };

  const renderPage = (page, pageIndex) => {
    if (!page) return null;

    if (viewMode === 'basic') {
      const snapshotPage = snapshots[pageIndex];
      if (!snapshotPage || !snapshotPage.snap_shot_url) {
        return (
          <div className="flex items-center justify-center h-full text-gray-500">
            Snapshot not available
          </div>
        );
      }
      return (
        <div className="absolute inset-0 flex items-center justify-center">
           {!loadedSnapshots[pageIndex] && (
            <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
              <div className="w-6 h-6 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}
          <img
            src={snapshotPage.snap_shot_url}
            alt="Snapshot page"
            className="w-[650px] h-[650px]"
            style={{ objectFit: 'fill' }}
            loading="lazy"
            onClick={() => setZoomedImage(snapshotPage.snap_shot_url)}
            onLoad={() =>
              setLoadedSnapshots(prev => ({ ...prev, [pageIndex]: true }))
            }
          />
        </div>
        
      );
    }
  
    //const visuals = page.visual_content ? JSON.parse(page.visual_content.replace(/'/g, '"')) : [];
    const visuals = parseVisualContent(page.visual_content);
    const hasText = page.page_content && page.page_content.trim().length > 0;
    const hasVisuals = visuals.length > 0;
  
    const pageHeight = 650;
    const safeMargin = 100;
    const textHeight = textHeights[pageIndex] || 0;
  
    const remainingHeight = pageHeight - textHeight - safeMargin;
    const visualMaxHeight = remainingHeight > 0 ? remainingHeight * 0.75 : 0;
  
    const isEditing = editingPages[pageIndex];

    const startEditing = (pageIdx) => {
      setEditingPages(prev => ({ ...prev, [pageIdx]: true }));
      setDraftTexts(prev => ({ ...prev, [pageIdx]: pages[pageIdx]?.page_content || '' }));
      setTimeout(() => {
        textRefs.current[pageIdx]?.focus();
      }, 0);
    };
    
    const saveEditing = async (pageIdx) => {
      const updatedPages = [...pages];
      const newText = draftTexts[pageIdx] || '';
      const pageId = updatedPages[pageIdx]?.page_meta_unique_id;
      setPageStatus(prev => ({ ...prev, [pageIdx]: 'saving' }));
      updatedPages[pageIdx] = { ...updatedPages[pageIdx], page_content: newText };
      setPages(updatedPages);
      try{
        const response = await request({
          method: 'patch',
          url: `/api/v1/pdf_parser/pages/${pageId}/`,
          data: { page_content: newText },
        });
      } catch (error) {
        console.error('API error during save:', error);
        alert('Something went wrong while saving. Please try again later!');
      } finally {
        setPageStatus(prev => ({ ...prev, [pageIdx]: 'idle' }));
      }
      setEditingPages(prev => ({ ...prev, [pageIdx]: false }));
      setDraftTexts(prev => {
        const { [pageIdx]: _, ...rest } = prev;
        return rest;
      });
    };
    
    const cancelEditing = (pageIdx) => {
      setEditingPages(prev => ({ ...prev, [pageIdx]: false }));
      setDraftTexts(prev => {
        const { [pageIdx]: _, ...rest } = prev;
        return rest;
      });
    };
  
    return (
      <div className="flex flex-col h-full gap-4 overflow-hidden relative">

        {(pageStatus[pageIndex] === 'saving' || pageStatus[pageIndex] === 'deleting') && (
          <div className="absolute w-[650px] h-[650px] bg-opacity-80 flex items-center justify-center z-50">
            <div className="text-center">
              <div className="animate-spin h-10 w-10 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
              <div className="text-sm font-semibold text-gray-600">
                {pageStatus[pageIndex] === 'saving' ? 'Saving...' : 'Deleting...'}
              </div>
            </div>
          </div>
        )}

        {/* Floating Toolbar - OUTSIDE contentEditable */}
        {hasText && isEditing && (
          <Draggable bounds="parent">
            <div className="absolute bottom-16 left-1/2 transform -translate-x-1/2 shadow rounded p-2 flex gap-2 z-20">
              <button
                onClick={() => saveEditing(pageIndex)}
                className="px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
              >
                Save
              </button>
              <button
                onClick={() => cancelEditing(pageIndex)}
                className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
              >
                Cancel
              </button>
              <button
              onClick={() => {
                setPageToDelete(pageIndex);
                setShowDeleteModal(true);
              }}
              className="px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
            >
              Delete
            </button>
            </div>
          </Draggable>
        )}
  
        {/* Editable/Read-Only Text Area */}
        {hasText && (
          <div className="relative text-left overflow-hidden">
            <div
            ref={(el) => (textRefs.current[pageIndex] = el)}
            contentEditable={isEditing}
            suppressContentEditableWarning
            onInput={(e) => {
              const newText = e.currentTarget.innerText;
              setDraftTexts(prev => ({ ...prev, [pageIndex]: newText }));
            }}
            onClick={() => !isEditing && startEditing(pageIndex)}
            className={`p-2 whitespace-pre-line break-words text-justify ${isEditing ? 'border border-blue-400' : ''}`}
            style={{ minHeight: '100px' }}
          >
            {isEditing ? draftTexts[pageIndex] : page.page_content}
          </div>
          </div>
        )}
  
        {/* Visuals */}
        {hasVisuals && visualMaxHeight > 50 && (
          <div
            className="flex flex-col justify-center items-center gap-4 overflow-hidden"
            style={{ maxHeight: visualMaxHeight }}
          >
            {visuals.map((src, idx) => (
              <img
                key={idx}
                src={src}
                alt={`Visual ${idx + 1}`}
                className="object-contain w-auto max-w-full max-h-full rounded shadow cursor-pointer"
                loading="lazy"
                onClick={() => {
                  setZoomedImage(src);
                  posthog.capture('zoom_visual', {
                    book_id,
                    page_index: pageIndex,
                    image_url: src
                  });
                }}
              />
            ))}
          </div>
        )}
      </div>
    );
  };
  
  useEffect(() => {
    if (currentPage !== targetPage) {
      const timer = setTimeout(() => {
        setCurrentPage(targetPage); // Update currentPage after the animation
        updateUserBookDetail({last_read_page:targetPage});
      }, 500); // Adjust this delay to match animation duration
      return () => clearTimeout(timer);
    }
  }, [targetPage, currentPage]);

  return (
    <div className="relative flex flex-col items-center h-screen bg-gray-200"
      style={{
        backgroundImage: `url(${selectedBackgroundTheme?.theme_url || '/images/wood.jpg'})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        boxShadow: '0px 8px 20px rgba(0, 0, 0, 0.15)'
      }}>

      {/* Initial Loader */}
      {initialLoading && (
        <div className="absolute inset-0 z-50 bg-white bg-opacity-80 flex items-center justify-center">
          <div className="animate-spin h-12 w-12 border-4 border-blue-500 border-t-transparent rounded-full"></div>
        </div>
      )}

      {showPremiumModal && previewTheme && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
            <h2 className="text-xl font-semibold mb-4 text-center">{previewTheme.theme_name}</h2>
            <img
              src={previewTheme.theme_url}
              alt={previewTheme.theme_name}
              className="w-full h-56 object-cover rounded mb-4"
            />
            {previewTheme.has_audio === 1 && (
              <audio controls className="w-full mb-4">
                <source src={previewTheme.audio_url} type="audio/mpeg" />
                Your browser does not support the audio element.
              </audio>
            )}
            <p className="text-sm text-gray-600 text-center mb-6">
              This is a premium theme. Upgrade to access it.
            </p>
            <div className="flex justify-center gap-4">
              <button
                className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded"
                onClick={() => window.location.href = '/pricing'}
              >
                Go Premium
              </button>
              <button
                className="text-gray-600 hover:text-gray-800"
                onClick={() => setShowPremiumModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg text-center space-y-4 w-[300px]">
              <h2 className="text-lg font-semibold">Delete Page?</h2>
              <p className="text-gray-600 text-sm">Are you sure you want to delete this page?</p>
              <div className="flex justify-center gap-4 mt-4">
                <button
                  onClick={() => {
                    deletePage(pageToDelete);
                    setShowDeleteModal(false);
                    setPageToDelete(null);
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Yes, Delete
                </button>
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setPageToDelete(null);
                  }}
                  className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
          )}

      {/* Settings Panel */}
        <div
          className={`fixed top-0 right-0 h-full w-72 bg-white shadow-lg p-6 z-40 flex flex-col gap-6 border-l border-gray-300 transform transition-transform duration-300 ${
            showSettings ? 'translate-x-0' : 'translate-x-full'
          }`}
          >
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Reader Settings</h2>
              <button
                onClick={() => setShowSettings(false)}
                className="text-gray-500 hover:text-red-500 text-xl"
              >
              &times;
            </button>
          </div>
          {/* Page Theme Dropdown */}
          <div>
            <label htmlFor="page-theme" className="block text-sm font-medium text-gray-600 mb-1">
              Page Theme
              </label>
                <select
                  id="page-theme"
                  value={selectedPageTheme?.theme_meta_unique_id || ''}
                  onChange={(e) => {
                    const theme = pageThemes.find(t => t.theme_meta_unique_id === e.target.value);
                    handlePageThemeChange(theme);
                  }}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                >
              {pageThemes.map(theme => (
                <option key={theme.theme_meta_unique_id} value={theme.theme_meta_unique_id}>
                  {theme.theme_name}
                </option>
              ))}
            </select>
          </div>
          {/* Background Theme Dropdown */}
          <div>
            <label htmlFor="background-theme" className="block text-sm font-medium text-gray-600 mb-1">
              Background Theme
            </label>
              <ThemeDropdownWithPreview
                themes={backgroundThemes}
                selectedTheme={selectedBackgroundTheme}
                setSelectedTheme={(theme) => {
                  setSelectedBackgroundTheme(theme);
                  handleBackgroundThemeChange(theme);
                }}
                onLockedThemeClick={(theme) => {
                  setPreviewTheme(theme);
                  setShowPremiumModal(true);
                  posthog.capture('click_locked_theme_preview', {
                    theme_name: theme.theme_name,
                    theme_id: theme.theme_meta_unique_id
                  });
                }}
              />
              {/*<select
                id="background-theme"
                value={selectedBackgroundTheme?.theme_meta_unique_id || ''}
                onChange={(e) => {
                  const theme = backgroundThemes.find(t => t.theme_meta_unique_id === e.target.value);
                  handleBackgroundThemeChange(theme);
                }}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              >
              {backgroundThemes.map(theme => (
                <option key={theme.theme_meta_unique_id} value={theme.theme_meta_unique_id}>
                  {theme.theme_name}
                </option>
              ))}
            </select>*/}
          </div>
          {/* Audio Control */}
          {selectedBackgroundTheme?.has_audio === 1 && selectedBackgroundTheme.audio_url && (
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Audio</label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={audioVolume}
                  onChange={(e) => setAudioVolume(parseFloat(e.target.value))}
                  className="w-full"
                />
                <button
                  onClick={() => setIsMuted(prev => !prev)}
                  className="mt-2 text-sm text-blue-600 underline"
                >
                {isMuted ? 'Unmute' : 'Mute'}
              </button>
            </div>
          )}
          {loadingTheme && (
            <div className="text-sm text-gray-700 flex items-center gap-2">
              <span>Loading <strong>{loadingTheme.theme_name}</strong>...</span>
              <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>

      {loadingSnapshots && (
        <div className="absolute top-4 right-4 z-50 bg-white bg-opacity-80 rounded-full p-2 shadow-lg">
          <div className="w-6 h-6 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}

      {zoomedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50"
          onClick={() => setZoomedImage(null)}
        >
          <img
            src={zoomedImage}
            alt="Zoomed Visual"
            className="max-w-4xl max-h-[90vh] rounded shadow-lg object-contain"
          />
        </div>
      )}
      {/* Book Title Header 
      <header className="w-full flex justify-center py-4">
        <h1 className="text-3xl font-serif text-stone-100">
          My Book Title
        </h1>
      </header>*/}

      {/* Main Content: Book and Table Surface */}
      <div className={`relative flex justify-center items-center h-full w-full transition-opacity duration-500 ${fade ? 'opacity-100' : 'opacity-0'}`}>
        {/* Book */}
        <div className={`relative flex justify-center items-center`}>
           {/* Book Shadow */}
           <div
            className="absolute w-[680px] h-[680px] bg-black rounded-full opacity-10"
            style={{ transform: 'translateY(10px)', filter: 'blur(15px)' }}
          ></div>
          {/* Left Page */}
          <div className={`bg-stone-100 w-[650px] h-[650px] border-r-4 border-${selectedPageTheme?.border_color ? selectedPageTheme.border_color:'gray-300'} rounded-l-md shadow-lg relative overflow-hidden ${targetPage !== currentPage ? (targetPage < currentPage ? 'flip-prev' : '') : ''}`}
          style={{
            transformOrigin: 'right center',
            backgroundImage: `url(${selectedPageTheme?.theme_url || '/images/clear-white-plaster-texture-pattern.jpg'})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.25)',
            zIndex: targetPage < currentPage ? 2 : 1, // Left page comes to top if flipping backwards
            }}>
            <div className="absolute inset-0 flex justify-start items-start text-md font-serif text-gray-800 p-8 leading-relaxed text-shadow whitespace-pre-line overflow-y-auto">
            {renderPage(leftPage, currentPage * 2)}
            </div>
            {/* Page Number for Left Page */}
            <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-md font-serif text-gray-800 leading-relaxed text-shadow">
               {currentPage * 2 + 1} {/* Calculate odd-numbered pages */}
            </div>
            <div className={`absolute top-0 left-0 w-3 h-full bg-${selectedPageTheme?.border_color ? selectedPageTheme.border_color:'gray-300'} shadow-inner`}></div>
            <div className={`absolute bottom-0 left-0 w-full h-1 bg-${selectedPageTheme?.border_color ? selectedPageTheme.border_color:'gray-300'} shadow-inner`}></div>
          </div>

          {/* stack page left */}
          <div className={`absolute left-0 w-[650px] h-[650px] bg-gray-100 rounded-r-md border-r-4 border-${selectedPageTheme?.border_color ? selectedPageTheme.border_color:'gray-300'} shadow-md`}
          style={{
            zIndex: 0, // Further below the flipping page
            backgroundImage: `url(${selectedPageTheme?.theme_url || '/images/clear-white-plaster-texture-pattern.jpg'})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            }}>
            <div className={`absolute inset-0 flex justify-start items-start text-md font-serif text-gray-800 p-8 leading-relaxed text-shadow`}>
            {renderPage(previousLeftPage, (currentPage - 1) * 2)}
            </div>
            <div className={`absolute top-0 left-0 w-3 h-full bg-${selectedPageTheme?.border_color ? selectedPageTheme.border_color:'gray-300'} shadow-inner`}></div>
            <div className={`absolute bottom-0 left-0 w-full h-1 bg-${selectedPageTheme?.border_color ? selectedPageTheme.border_color:'gray-300'} shadow-inner`}></div>
          </div>
          
          {/* Book Binding */}
          <div className="w-[10px] h-[650px] bg-gray-800"></div>

          {/* stack page right */}
          <div className={`absolute right-0 w-[650px] h-[650px] bg-gray-100 rounded-r-md border-l-4 border-${selectedPageTheme?.border_color ? selectedPageTheme.border_color:'gray-300'} shadow-md`}
          style={{
            zIndex: 0, // Further below the flipping page
            backgroundImage: `url(${selectedPageTheme?.theme_url || '/images/clear-white-plaster-texture-pattern.jpg'})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            }}>
            <div className="absolute inset-0 flex justify-start items-start text-md font-serif text-gray-800 p-8 leading-relaxed text-shadow">
            {renderPage(nextRightPage, (currentPage + 1) * 2 + 1)}
            </div>
            <div className={`absolute top-0 right-0 w-3 h-full bg-${selectedPageTheme?.border_color ? selectedPageTheme.border_color:'gray-300'} shadow-inner`}></div>
            <div className={`absolute bottom-0 right-0 w-full h-1 bg-${selectedPageTheme?.border_color ? selectedPageTheme.border_color:'gray-300'} shadow-inner`}></div>
          </div>

          {/* Right Page */}
          <div className={`bg-stone-100 w-[650px] h-[650px] border-l-4 border-${selectedPageTheme?.border_color ? selectedPageTheme.border_color:'gray-300'} rounded-r-md shadow-lg relative overflow-hidden ${targetPage !== currentPage ? (targetPage > currentPage ? 'flip-next' : '') : ''}`}
          style={{
            transformOrigin: 'left center',
            backgroundImage: `url(${selectedPageTheme?.theme_url || '/images/clear-white-plaster-texture-pattern.jpg'})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.25)',
            zIndex: targetPage > currentPage ? 3 : 1, // Right page comes to top if flipping forwards
            }}>
            <div className="absolute inset-0 flex justify-start items-start text-md font-serif text-gray-800 p-8 leading-relaxed text-shadow">
            {renderPage(rightPage, currentPage * 2 + 1)}
            </div>
            <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-md font-serif text-gray-800 leading-relaxed text-shadow">
               {currentPage * 2 + 2} {/* Calculate even-numbered pages */}
            </div>
            <div className={`absolute top-0 right-0 w-3 h-full bg-${selectedPageTheme?.border_color ? selectedPageTheme.border_color:'gray-300'} shadow-inner`}></div>
            <div className={`absolute bottom-0 right-0 w-full h-1 bg-${selectedPageTheme?.border_color ? selectedPageTheme.border_color:'gray-300'} shadow-inner`}></div>
          </div>
        </div>
      </div>
      {/* Basic View Link */}
      <div className="absolute top-0 right-16 z-20">
        {viewMode === 'enhanced' ? (
          <button
            onClick={switchToBasicView}
            className="text-sm text-blue-600 underline hover:text-blue-800"
          >
            Switch to Basic View
          </button>
          ) : (
          <button
            onClick={backToEnhancedView}
            className="text-sm text-blue-600 underline hover:text-blue-800"
          >
            Back to Enhanced View
          </button>
        )}
      </div>
      {/* Gear Icon */}
      <div className="absolute top-0 right-0 z-30">
      <button
          onClick={() => {
            setShowSettings(true);
            posthog.capture('toggle_settings_panel', {
              book_id,
              opened: true
            });
          }}
          className="p-3 text-gray-700 hover:text-blue-600 text-3xl"
          aria-label="Settings"
        >
          ⚙️
        </button>
      </div>
      {/* Navigation Buttons */}
      <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 flex space-x-4">
        <button onClick={previousPage}
        className="w-16 h-16 bg-transparent border-2 border-blue-500 text-blue-500 rounded-full flex justify-center items-center shadow-md text-xl hover:bg-blue-500 hover:text-white transition-all">
          &lt;
        </button>
        <button onClick={nextPage} 
        className="w-16 h-16 bg-transparent border-2 border-blue-500 text-blue-500 rounded-full flex justify-center items-center shadow-md text-xl hover:bg-blue-500 hover:text-white transition-all">
          &gt;    
        </button>
      </div>
      {selectedBackgroundTheme && (
        <audio
        ref={audioRef}
        src={selectedBackgroundTheme.audio_url}
        loop
        autoPlay
        muted={isMuted}
        volume={audioVolume}
      />
      )}
    </div>
  );
}

export default BasicTheme;
